import { WaypointCategoryEnum } from "../../enums";
import {
  IRoutingWaypointStationRow,
  IStationRouteStationInfo,
  IStationRouteWaypointStationLike,
  TRoutingWaypointStationInput,
} from "../../models";
import {
  filterRealRouteStations,
  isStationRouteWaypointStation,
} from "../routing-waypoint";
import { areStationRowsEqual } from "../routing-waypoint/routing-waypoint-station-row.helpers";
import { WAYPOINT_CATEGORY_VISUAL } from "./station-route.constants";
import { IWaypointCategoryVisual } from "./station-route.models";

/** Map ride/course station rows to the shape expected by waypoint map UI helpers. */
export const toWaypointMapStations = (
  stations: IStationRouteWaypointStationLike[],
): IStationRouteStationInfo[] =>
  stations.map(station => ({
    id: station.id,
    name: station.name,
    locationInfo: station.locationInfo,
    areaId: station.areaId,
    timeArrival: station.timeArrival === "" ? undefined : station.timeArrival,
    waypointCategory: station.waypointCategory,
  }));

/** 1-based map/list label for a real station in a mixed route; undefined for waypoints. */
export const getRealStationMarkerNumber = ({
  locations,
  locationIndex,
}: {
  locations?: TRoutingWaypointStationInput[];
  locationIndex: number;
}): number | undefined => {
  const location = locations?.[locationIndex];

  if (!location || isStationRouteWaypointStation(location)) {
    return undefined;
  }

  return (locations?.slice(0, locationIndex + 1) ?? []).filter(
    routeLocation => !isStationRouteWaypointStation(routeLocation),
  ).length;
};

/** True when the location is a real station and no later real station exists in the list. */
export const isLastRealRouteLocation = ({
  locations,
  locationIndex,
}: {
  locations?: TRoutingWaypointStationInput[];
  locationIndex: number;
}): boolean => {
  const location = locations?.[locationIndex];

  if (!location || isStationRouteWaypointStation(location)) {
    return false;
  }

  for (
    let index = locationIndex + 1;
    index < (locations?.length ?? 0);
    index++
  ) {
    if (!isStationRouteWaypointStation(locations?.[index])) {
      return false;
    }
  }

  return true;
};

/** 1-based passenger station label in a mixed route list; undefined for waypoints. */
export const getRealStationListOrdinal = ({
  station,
  stations,
  listIndex,
}: {
  station?: IRoutingWaypointStationRow;
  stations?: IRoutingWaypointStationRow[];
  listIndex: number;
}): number | undefined => {
  if (!station || isStationRouteWaypointStation(station)) {
    return undefined;
  }

  const ordinal = filterRealRouteStations(stations).findIndex(item =>
    areStationRowsEqual({ a: item, b: station }),
  );

  return ordinal >= 0 ? ordinal + 1 : listIndex + 1;
};

/** True when waypoint ids changed position in a drag reorder (composed or raw station ids). */
export const didRoutingWaypointOrderChange = <TId>({
  previousOrderedIds,
  nextOrderedIds,
  isWaypointId,
}: {
  previousOrderedIds: TId[];
  nextOrderedIds: TId[];
  isWaypointId: (id: TId) => boolean;
}): boolean => {
  if (previousOrderedIds.length !== nextOrderedIds.length) {
    return true;
  }

  const previousWaypointIds = previousOrderedIds.filter(isWaypointId);
  const nextWaypointIds = nextOrderedIds.filter(isWaypointId);

  if (previousWaypointIds.length !== nextWaypointIds.length) {
    return true;
  }

  if (previousWaypointIds.some((id, index) => id !== nextWaypointIds[index])) {
    return true;
  }

  return previousOrderedIds.some(
    (id, index) => isWaypointId(id) !== isWaypointId(nextOrderedIds[index]),
  );
};

/** Badge / map pin colors + i18n tooltip key by waypoint category. */
export const getWaypointCategoryVisual = (
  category?: WaypointCategoryEnum,
): IWaypointCategoryVisual =>
  WAYPOINT_CATEGORY_VISUAL[category ?? WaypointCategoryEnum.MLPathWaypoint] ??
  WAYPOINT_CATEGORY_VISUAL[WaypointCategoryEnum.MLPathWaypoint];
